import * as React from "react"

import { ExternalLink } from "lucide-react"

import type { ResourceMenuAction } from "../ResourceMenu/ResourceMenu"
import type { RowAction } from "./types"

/**
 * Runs a selected `skipFocusReturn` action once the menu has closed, in place of the menu returning focus to its trigger. While the menu is open it holds focus inside itself, so a field the action opened any earlier would lose focus to it.
 */
export function useSkipFocusReturn<T>(actions: RowAction<T>[]) {
  const pendingActionRef = React.useRef<(() => void) | null>(null)
  const withFocusControl = (items: RowAction<T>[]): RowAction<T>[] =>
    items.map((action) => ({
      ...action,
      onSelect: action.skipFocusReturn
        ? (row: T) => {
            pendingActionRef.current = () => action.onSelect(row)
          }
        : action.onSelect,
      items: action.items ? withFocusControl(action.items) : undefined,
    }))
  const onCloseAutoFocus = (event: Event) => {
    const pendingAction = pendingActionRef.current
    if (pendingAction) {
      pendingActionRef.current = null
      event.preventDefault()
      pendingAction()
    }
  }
  return { actions: withFocusControl(actions), onCloseAutoFocus }
}

export function resolveRowActions<T>(
  row: T,
  actions: RowAction<T>[],
): ResourceMenuAction[] {
  return actions.map((action) => ({
    ...action,
    disabled:
      typeof action.disabled === "function"
        ? action.disabled(row)
        : action.disabled,
    onSelect: () => action.onSelect(row),
    items: action.items ? resolveRowActions(row, action.items) : undefined,
  }))
}

/**
 * Leads a row's menu with "Open in new tab" in a section of its own, when the
 * row has a path to open.
 */
export function withOpenInNewTab(
  actions: ResourceMenuAction[],
  onOpenInNewTab: (() => void) | undefined,
): ResourceMenuAction[] {
  if (!onOpenInNewTab) {
    return actions
  }
  const [first, ...rest] = actions
  return [
    {
      label: "Open in new tab",
      icon: <ExternalLink />,
      onSelect: onOpenInNewTab,
      "data-testid": "table-row-open-in-new-tab",
    },
    ...(first ? [{ ...first, separatorBefore: true }, ...rest] : []),
  ]
}
